/**
 * CopyConfigButton — copies a row's optimiser recipe into the clipboard.
 * Only CUSTOM rows can be copied (STANDARD has no recipe). Shows a transient
 * check on click and stays highlighted while this row is the copied source.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useState, type FC } from 'react';
import { Check, Copy } from 'lucide-react';

import { Button, Tooltip } from '@/components/ui';
import type { OptimizerRow } from '@/features/optimizer-center/optimizer-center.types';
import { cn } from '@/lib/utils';

export interface CopyConfigButtonProps {
  row: OptimizerRow;
  isCopiedSource: boolean;
  onCopy: (row: OptimizerRow) => void;
}

export const CopyConfigButton: FC<CopyConfigButtonProps> = ({ row, isCopiedSource, onCopy }) => {
  const [justCopied, setJustCopied] = useState(false);
  const isStandard = row.mode === 'STANDARD';

  const handleClick = () => {
    onCopy(row);
    setJustCopied(true);
    window.setTimeout(() => setJustCopied(false), 1500);
  };

  const label = isStandard
    ? 'Only TrafficPulse Optimisation configs can be copied'
    : 'Copy this optimizer config (paste onto selected flows)';

  return (
    <Tooltip content={label} side="bottom">
      <Button
        variant="ghost"
        size="icon"
        className={cn('h-7 w-7', isCopiedSource && 'text-primary')}
        aria-label="Copy optimizer config"
        title={label}
        disabled={isStandard}
        onClick={handleClick}
      >
        {justCopied ? (
          <Check className="h-4 w-4 text-success" aria-hidden />
        ) : (
          <Copy className="h-4 w-4" aria-hidden />
        )}
      </Button>
    </Tooltip>
  );
};
